import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from '@mlm/ui';
import { courseBadge } from '@mlm/shared';
import type { CourseSummary } from '../../lib/api';
import { fmtDuration, peso } from '../lib/format';
import { useProductHref, useStorefrontContent } from '../lib/hooks';
import { learningPlaceholder } from '../lib/learning-placeholder';
import type { DisplayProduct } from '../lib/types';
import { VirtualHoverPanel } from './product-card';
import { Placeholder } from './placeholder';
import { WishlistHeart } from './wishlist-heart';

/**
 * Learning-storefront course tile — BORDERLESS (no card box): a 16:9 cover with
 * a soft radius and NO border, the badge overlaid on the cover's top-left, the
 * save heart on its top-right, then display-face title (2-line clamp), the
 * instructor, a rating + short meta line, and the price in learning GREEN.
 *
 * Two deliberate departures from the marketplace card: the price is brand
 * green rather than ink, and the badge sits on the image instead of under the
 * text block — the text column reads as one paragraph that way.
 *
 * The badge is DERIVED from the course row (`courseBadge`), never handed in by
 * a caller counting array positions — the old `i === 0` "New" stopped being
 * true the moment the sort changed, and the featured flag was labelled as a
 * sales ranking nobody had measured.
 */
export function CourseTile({
  product: p,
  course,
  index = 0,
}: {
  product: DisplayProduct;
  course: CourseSummary;
  index?: number;
}) {
  const hrefFor = useProductHref();
  const showRatings = useStorefrontContent().data.showCourseRatings;
  const href = hrefFor(p);
  const rating = course.ratingCount > 0 ? Number(course.ratingAvg) : null;
  const badge = courseBadge(course);

  const tile = (
    <Link to={href} className="group block" data-testid="course-tile">
      <div className="relative">
        <div className="aspect-video overflow-hidden rounded-xl bg-white">
          {p.image ? (
            <img src={p.image} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            // A course with no cover of its own gets a shipped stock photo
            // rather than a striped block — decoration, never presented as this
            // course's own picture, and replaced the moment one is uploaded.
            <Placeholder
              i={index}
              label="course cover"
              src={learningPlaceholder(index)}
              className="h-full w-full"
            />
          )}
        </div>
        {badge && (
          <span
            className={`absolute left-2.5 top-2.5 rounded-full px-[11px] py-1 text-[11.5px] font-bold ${
              badge === 'featured' ? 'bg-lms-gold text-lms-ink' : 'bg-lms text-lms-cream'
            }`}
          >
            {badge === 'featured' ? 'Featured' : 'New'}
          </span>
        )}
        <WishlistHeart productId={p.id} noun="course" className="absolute right-2.5 top-2.5" />
      </div>
      <h3 className="mt-[11px] line-clamp-2 font-lms-heading text-[16.5px] font-bold leading-[1.28] tracking-[-0.01em] text-lms-ink group-hover:text-lms">
        {p.name}
      </h3>
      {course.creatorName && (
        <div className="mt-1 text-[12.5px] text-lms-muted">{course.creatorName}</div>
      )}
      <div className="mt-[5px] flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-lms-muted">
        {showRatings && rating != null && (
          <>
            <Star className="h-[13px] w-[13px] fill-lms-gold text-lms-gold" />
            <span className="font-bold text-lms-gold-deep">{rating.toFixed(1)}</span>
            <span>({course.ratingCount.toLocaleString()})</span>
            <span className="text-lms-hairline">·</span>
          </>
        )}
        <span>
          {course.lessonCount} module{course.lessonCount === 1 ? '' : 's'}
          {course.durationSeconds > 0 ? ` · ${fmtDuration(course.durationSeconds)}` : ''}
          {course.level ? ` · ${cap(course.level)}` : ''}
        </span>
      </div>
      <div className="mt-[7px] flex items-baseline gap-2">
        {course.accessLevel === 'free' ? (
          <span className="font-lms-heading text-[17px] font-bold text-lms">Free</span>
        ) : course.accessLevel === 'subscriber' ? (
          <span className="font-lms-heading text-[17px] font-bold text-lms">Subscribers</span>
        ) : (
          <>
            <span className="font-lms-heading text-[17px] font-bold text-lms">{peso(p.price)}</span>
            {p.was > p.price && (
              <span className="text-[12.5px] text-lms-muted line-through">{peso(p.was)}</span>
            )}
          </>
        )}
      </div>
    </Link>
  );

  return (
    <HoverCard openDelay={250} closeDelay={100}>
      <HoverCardTrigger asChild>{tile}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          side="right"
          align="start"
          collisionPadding={12}
          className="omni-store w-72"
        >
          <VirtualHoverPanel p={p} detailHref={href} />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}

/** Skeleton tile matching the tile geometry (cover block + 3 text lines). */
export function CourseTileSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="aspect-video rounded-xl bg-lms-hairline/60" />
      <div className="mt-[11px] h-4 w-11/12 rounded bg-lms-hairline/60" />
      <div className="mt-1.5 h-3 w-1/2 rounded bg-lms-hairline/60" />
      <div className="mt-2 h-4 w-1/3 rounded bg-lms-hairline/60" />
    </div>
  );
}

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
